<template>
    <ModalShell :title="title" :subtitle="subtitle">
        <template #icon>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H6C4.93913 15 3.92172 15.4214 3.17157 16.1716C2.42143 16.9217 2 17.9391 2 19V21M19 8V14M22 11H16M13 7C13 9.20914 11.2091 11 9 11C6.79086 11 5 9.20914 5 7C5 4.79086 6.79086 3 9 3C11.2091 3 13 4.79086 13 7Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
        </template>
        <!-- Form fields -->
        <div class="form-section">
            <!-- First Name and Last Name row -->
            <div class="form-row">
                <ModalField
                    class="flex-1"
                    label="First Name"
                    required
                    input-id="add-contact-first-name"
                    error-id="add-contact-name-error"
                    :error="nameExists ? 'This family member already exists.' : undefined"
                    v-slot="{ errorId, invalid }"
                >
                    <PrimeInputText
                        id="add-contact-first-name"
                        v-model="firstName"
                        class="w-full"
                        placeholder="Jane"
                        :invalid="invalid"
                        aria-required="true"
                        :aria-describedby="errorId"
                        @keydown.enter="onEnter"
                        @keydown="letterOnlyInputHandler"
                        @paste="letterOnlyInputHandler"
                    />
                </ModalField>
                <ModalField class="flex-1" label="Last Name" required input-id="add-contact-last-name">
                    <PrimeInputText
                        id="add-contact-last-name"
                        v-model="lastName"
                        class="w-full"
                        placeholder="Doe"
                        :invalid="nameExists"
                        aria-required="true"
                        :aria-describedby="nameExists ? 'add-contact-name-error' : undefined"
                        @keydown.enter="onEnter"
                        @keydown="letterOnlyInputHandler"
                        @paste="letterOnlyInputHandler"
                    />
                </ModalField>
            </div>

            <!-- Email Address: optional to add a member, and what a license is delivered to -->
            <ModalField
                label="Email Address"
                :required="!!member"
                input-id="add-contact-email"
                :error="emailError"
                v-slot="{ errorId, invalid }"
            >
                <PrimeInputText
                    id="add-contact-email"
                    v-model="email"
                    class="w-full"
                    placeholder="jane.doe@email.com"
                    :invalid="invalid"
                    :aria-required="member ? 'true' : undefined"
                    :aria-describedby="errorId"
                    @keydown.enter="onEnter"
                />
            </ModalField>

            <ModalField label="Phone Number" input-id="add-contact-phone">
                <PhoneFields v-model="phone" input-id="add-contact-phone" @update:valid="isPhoneValid = $event" />
            </ModalField>
        </div>
        <template #footer>
            <PrimeButton
                label="Cancel"
                class="p-btn-root_secondary"
                @click="closeDialog()"
            />
            <PrimeButton
                :label="saveLabel"
                variant="primary"
                :disabled="!canAddContact"
                :loading="isAddingContact"
                @click="addContact"
            />
        </template>
    </ModalShell>
</template>

<script setup lang="ts">
import { isValidEmail } from '@privacy-bee/validate/email';
import { normalizePhone } from '@privacy-bee/validate/phone';
import PrimeInputText from 'primevue/inputtext';
import ModalField from '~/components/modals/shared/ModalField.vue';
import ModalShell from '~/components/modals/shared/ModalShell.vue';
import PhoneFields from '~/components/shared/forms/PhoneFields.vue';
import { useDynamicDialog } from '~/composables/dynamic-dialog';
import { useContactsStore } from '~/stores/person/contacts';
import { useVaultStore } from '~/stores/person/vault/vault';
import { ContactType, type ContactCreateResult } from '~/types/person/contacts';
import { ModalNames } from '~/types/modals';

const { letterOnlyInputHandler } = useUtils();
const { isMobile } = useIsMobile();
const { lockScroll, unlockScroll } = useScrollLock();
const contactsStore = useContactsStore();
const vaultStore = useVaultStore();

const dialogRef = getDialogRef(ModalNames.ADD_CONTACT);
const modalData = computed(() => dialogRef.value?.data || {});

// Form state
const firstName = ref('');
const lastName = ref('');
const email = ref('');
const phone = ref({ country_code: '1', phone_number: '' });
const isPhoneValid = ref(false);
const isAddingContact = ref(false);

// Present when the form was opened on a member the family already holds, to complete them.
const member = computed(() => modalData.value?.member);
const reason = computed(() => modalData.value?.reason ?? 'direct');

const title = computed(() => (member.value ? `Complete ${member.value.name}'s Details` : 'Add Family Member'));

const subtitle = computed(() => {
    if (!member.value) return 'Add a new family member to your protection plan.';

    const name = member.value.name;
    if (reason.value === 'assign') return `A license is delivered to ${name}'s own email address. Save to add it and assign the license.`;
    if (reason.value === 'purchase') return `A license is delivered to ${name}'s own email address. Save to add it — you can purchase their license next.`;

    return `${name} needs an email address before they can receive a license.`;
});

// Only the free path carries on past the save. Buying never starts as a side effect of saving.
const saveLabel = computed(() => {
    if (!member.value) return 'Add Member';

    return reason.value === 'assign' ? 'Save & Assign License' : 'Save';
});

/**
 * Leaving with a result, or leaving with none. A caller that opened this form to attribute
 * something to a new member needs the member itself, and only this component ever holds it:
 * the create endpoint answers with nothing, so a caller left to find the new row would be
 * searching a list it did not write.
 *
 * The result carries `created` separately from the row, so a caller can tell a DISMISSAL (no
 * result at all) from a member that was created but could not be read back. Those two need
 * different sentences: one changed nothing, the other changed the account.
 */
const closeDialog = (result?: ContactCreateResult) => {
    dialogRef.value.close(result);
};

// Everyone but the member being completed: their own name and address are already in the list,
// and counting them as a duplicate of themselves would refuse every save this form exists to make.
const otherMembers = computed(() => (contactsStore.contacts || []).filter(contact => contact.id !== member.value?.id));

// Whether the account ALREADY holds this member. Read from the whole list, never the filtered
// one: that computed is narrowed by the /family page's search box, and a guard that cannot see
// past a search happily creates the duplicate it exists to prevent.
const nameExists = computed(() => {
    const fullName = `${firstName.value.trim()} ${lastName.value.trim()}`.toLowerCase();
    return otherMembers.value.some(contact => fullName === contact.name?.toLowerCase());
});

// Case-insensitive, and against the whole list for the same reason as the name above.
const emailExists = computed(() => {
    const emailLower = email.value.trim().toLowerCase();
    if (!emailLower) return false;
    return otherMembers.value.some(contact => contact.emails?.some(e => e.email?.toLowerCase() === emailLower));
});

// Read from the trimmed value, which is what the save gate and the payload both use: spaces are an
// empty optional field, and an error shown beside a live Save is the form disagreeing with itself.
const emailError = computed(() => {
    const trimmedEmail = email.value.trim();

    if (!trimmedEmail) return undefined;
    if (!isValidEmail(trimmedEmail)) return 'Please enter a valid email address.';
    if (emailExists.value) return 'This family member already exists.';
    return undefined;
});

// Computed: check if contact already exists (either name or email)
const contactExists = computed(() => nameExists.value || emailExists.value);

// A phone number with something typed must be a whole one: the field already shows a partial
// number as invalid, and saving past it would drop what was typed without a word.
const phoneDigits = computed(() => normalizePhone(phone.value.phone_number));
// Asked of the raw value, because a lone trunk prefix normalises to nothing: reading "did they
// type a number?" off the normalised form treats a typed 0 as an empty field and drops it on save.
const phoneTyped = computed(() => /\p{Nd}/u.test(phone.value.phone_number));
const phoneIncomplete = computed(() => phoneTyped.value && !isPhoneValid.value);

// A whole name is always required. An address is optional when adding a member and required when
// completing one: this form is only ever opened on a member who is missing the address a license
// is delivered to, and saving without one would leave them exactly as incomplete as before.
const canAddContact = computed(() => {
    const trimmedEmail = email.value.trim();
    const emailAcceptable = member.value
        ? isValidEmail(trimmedEmail)
        : trimmedEmail.length === 0 || isValidEmail(trimmedEmail);

    return firstName.value.trim().length > 0 &&
           lastName.value.trim().length > 0 &&
           emailAcceptable &&
           !phoneIncomplete.value &&
           !contactExists.value;
});

// Fill the form from the member being completed. A stored name splits at its first space, so a
// one-word name leaves the last name empty for the reader to finish rather than inventing one.
watch(member, (existing) => {
    if (!existing) return;

    const [first = '', ...rest] = (existing.name ?? '').trim().split(/\s+/);
    firstName.value = first;
    lastName.value = rest.join(' ');
    email.value = existing.emails?.[0]?.email ?? '';

    const stored = existing.phone_numbers?.[0];
    phone.value = stored
        ? { country_code: String(stored.country_code), phone_number: stored.phone_number }
        : { country_code: '1', phone_number: '' };
}, { immediate: true });

// Watch for modal data changes and prefill relative information
watch(() => modalData.value?.contact, (relativeData) => {
    if (relativeData && typeof relativeData === 'object') {
        firstName.value = relativeData.first_name || '';
        lastName.value = relativeData.last_name || '';
    }
}, { immediate: true });

// A scan result carries one detail; the user completes the remaining form fields.
watch(() => modalData.value?.prefill, (prefill) => {
    if (!prefill) return;

    if (prefill.first_name) firstName.value = prefill.first_name;
    if (prefill.last_name) lastName.value = prefill.last_name;
    if (prefill.email) email.value = prefill.email;
    if (prefill.phone) phone.value = { ...prefill.phone };
}, { immediate: true });

const onEnter = (e: KeyboardEvent) => {
    e.preventDefault();
    if (canAddContact.value && !isAddingContact.value) {
        addContact();
    }
};

async function addContact() {
    if (isAddingContact.value || contactExists.value) return;

    isAddingContact.value = true;
    const fullName = `${firstName.value.trim()} ${lastName.value.trim()}`;
    const typedEmail = email.value.trim();
    const typedPhone = phoneDigits.value && isPhoneValid.value
        ? [{ country_code: Number(phone.value.country_code), phone_number: phoneDigits.value }]
        : [];

    if (member.value) {
        await completeMember(fullName, typedEmail, typedPhone);
        return;
    }

    const postData = {
        name: fullName,
        emails: typedEmail ? [typedEmail] : [],
        phone_numbers: typedPhone,
        tags: ['family'],
        blocked: false,
    };

    const { created, contact } = await contactsStore.createContact(postData);

    isAddingContact.value = false;

    // The POST is what decides whether this form is done. A member that now exists but could not
    // be read back is still created, and leaving the form open on it would show the reader a
    // duplicate warning about the person they just added, with a button that refuses to press.
    if (!created) return;

    // If this came from an exposure, suppress the original exposure
    if (modalData.value?.fromExposure && modalData.value?.exposureData) {
        try {
            const exposureData = modalData.value.exposureData;
            const exposureType = exposureData.type || 'relative';
            await vaultStore.updateExposure(true, exposureType, exposureData.value);
            await vaultStore.updatePotentialExposures(exposureType, exposureData.value, true);
            await vaultStore.fetchPotentialExposures();
        } catch (error) {
            console.error('Error suppressing exposure:', error);
        }
    }

    // `undefined` where the row could not be resolved: the caller asked for the member, and a
    // caller told nothing is a caller that can say what it could not do.
    closeDialog({ created, contact });
}

/** The member's addresses, which is not the same as their address entries: one can carry no value. */
const heldAddresses = (subject: { emails?: Array<{ email: string | null }> }): string[] =>
    (subject.emails ?? []).map(entry => entry.email).filter((email): email is string => !!email?.trim());

/**
 * Save an existing member. The endpoint replaces a list it is sent, so the whole member goes: the
 * one address and number this form shows, plus any others the member already held behind them.
 */
async function completeMember(
    name: string,
    typedEmail: string,
    typedPhone: Array<{ country_code: number; phone_number: string }>,
) {
    const existing = member.value!;
    const updated = await contactsStore.updateContact(existing.id, {
        name,
        // An entry carrying no value is not an address the endpoint would accept, so it is not
        // one of the member's addresses to carry forward.
        emails: [...(typedEmail ? [typedEmail] : []), ...heldAddresses(existing).slice(1)],
        phone_numbers: [
            ...typedPhone,
            ...(existing.phone_numbers ?? []).slice(1).map(({ country_code, phone_number }) => ({ country_code, phone_number })),
        ],
    });

    isAddingContact.value = false;

    // The store has already said what went wrong; the reader stays on the form to try again.
    if (!updated) return;

    const continueToAssign = reason.value === 'assign';
    closeDialog();

    if (continueToAssign) {
        useDynamicDialog(ModalNames.GIVE_LICENSE, {
            contact: { type: ContactType.CONTACT, id: updated.id, name: updated.name, is_self: false },
        });
    }
}

onMounted(() => {
    if (isMobile.value) {
        lockScroll();
    }
});

onUnmounted(() => {
    unlockScroll();
});
</script>

<style lang="scss" scoped>
.form-section {
    @apply flex flex-col gap-4 w-full;
}

.form-row {
    @apply flex sm:flex-row flex-col gap-4;
}

</style>
